import { useTranslation } from 'react-i18next';
import { ChevronDown, LogOut } from 'lucide-react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GLASS_SURFACE } from '@/components/shared/glass';
import { cn } from '@/lib/utils';

/**
 * Top-bar user menu. The glass chip is the avatar (no nested circle): a
 * 36px square with the initial, widening to a pill with the name and a
 * chevron on xl.
 */
export default function UserMenu({
    name,
    email,
    onLogout,
    showName = true,
}: {
    name?: string | null;
    email?: string | null;
    onLogout: () => void;
    showName?: boolean;
}) {
    const { t } = useTranslation('crm');
    const displayName = name || 'User';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    aria-label={t('chrome.accountMenu')}
                    className={cn(
                        GLASS_SURFACE,
                        'flex h-9 w-9 items-center justify-center gap-2 rounded-md outline-none transition-colors hover:bg-background focus-visible:ring-2 focus-visible:ring-ring',
                        showName && 'xl:w-auto xl:px-2.5'
                    )}
                >
                    <span className="text-sm font-semibold leading-none text-primary">
                        {displayName.charAt(0).toUpperCase()}
                    </span>
                    {showName && (
                        <>
                            <span className="hidden max-w-[10rem] truncate text-[13px] font-medium text-foreground xl:inline">
                                {displayName}
                            </span>
                            <ChevronDown
                                aria-hidden="true"
                                className="hidden size-3.5 text-muted-foreground xl:inline"
                            />
                        </>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                    <span className="block truncate text-sm font-medium">{displayName}</span>
                    {email && (
                        <span className="block truncate text-xs text-muted-foreground">
                            {email}
                        </span>
                    )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onLogout} className="gap-2">
                    <LogOut className="h-4 w-4" />
                    {t('chrome.logOut')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
